import React, { useEffect, useMemo, useState } from 'react';
import { hrmsAllAttendance, hrmsListEmployees } from '../../lib/hrmsApi';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const AttendancePage = () => {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([
        hrmsAllAttendance({ month }).catch(() => []),
        hrmsListEmployees().catch(() => []),
      ]);
      setRows(a);
      setEmps(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]);

  const days = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
  }, [month]);

  const map = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[`${r.employee_id}|${r.date}`] = r; });
    return m;
  }, [rows]);

  return (
    <div data-testid="attendance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold">Attendance</h1>
          <p className="text-sm text-slate-500">Daily clock-in/out sheet · click a cell to see details.</p>
        </div>
        <div>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10" data-testid="attendance-month" />
        </div>
      </div>

      <div className="mt-6 rounded-2xl border bg-white overflow-auto">
        <table className="text-xs min-w-max">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left sticky left-0 bg-slate-100 px-3 py-2 z-10">Employee</th>
              {days.map((d) => <th key={d} className="px-1 py-2 text-slate-600 font-normal">{d.slice(-2)}</th>)}
              <th className="px-2 py-2">Present</th>
            </tr>
          </thead>
          <tbody>
            {emps.filter(Boolean).map((emp) => {
              const present = days.filter((d) => map[`${emp.employee_id}|${d}`]?.clock_in).length;
              return (
                <tr key={emp.id} className="border-t">
                  <td className="sticky left-0 bg-white px-3 py-2 z-10 whitespace-nowrap">
                    <div className="font-semibold text-slate-900 text-xs">{emp.full_name}</div>
                    <div className="text-[10px] text-slate-500">{emp.employee_id}</div>
                  </td>
                  {days.map((d) => {
                    const rec = map[`${emp.employee_id}|${d}`];
                    let bg = 'bg-slate-100 text-slate-400';
                    let txt = '·';
                    if (rec?.clock_in && rec?.clock_out) { bg = 'bg-emerald-500 text-white'; txt = 'P'; }
                    else if (rec?.clock_in) { bg = 'bg-blue-500 text-white'; txt = 'I'; }
                    return <td key={d} className="px-1 py-1"><span className={`inline-flex h-6 w-6 items-center justify-center rounded text-[10px] font-bold ${bg}`} title={rec ? `${rec.clock_in || ''} → ${rec.clock_out || 'in progress'} · ${rec.hours || 0}h` : ''}>{txt}</span></td>;
                  })}
                  <td className="px-2 py-2 font-semibold text-slate-900">{present}/{days.length}</td>
                </tr>
              );
            })}
            {emps.length === 0 && !loading && <tr><td colSpan={days.length + 2} className="text-center text-slate-500 py-10">No employees onboarded yet.</td></tr>}
            {loading && emps.length === 0 && <tr><td colSpan={days.length + 2} className="text-center text-slate-400 py-10">Loading attendance…</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-xs text-slate-500 flex gap-3">
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-500" /> Present (full day)</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-500" /> Clocked-in only</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-200" /> No record</span>
      </div>
    </div>
  );
};

export default AttendancePage;
